import { FC } from 'react';
import { EMPTY_RESULT, formatScoreWithUnit, getExpertReportResultText } from '@/entities/expert-report';
import { EvaluationBlockRatingEditor } from './EvaluationBlockRatingEditor';
import { EvaluationBlockValueEditor } from './EvaluationBlockValueEditor';
import type { FlatRow } from './ExpertReportTab';

type Props = {
  row: FlatRow;
  conversationId: number;
  expertReportId: number;
};

/**
 * Результат строки заключения: балл вида `рейтинг / максимум` с размерностью, если она определена,
 * значение справочника, набор таких значений — или прочерк, когда результата нет.
 *
 * Блок, оценённый значением или набором, максимума и размерности не имеет и в числовые итоги
 * не входит. Правятся результаты только тех блоков, которые оценивает система сама: у итога и у
 * блока с вложенными блоками результат складывается по правилу расчёта и остаётся текстом.
 */
export const EvaluationBlockResult: FC<Props> = ({ row, conversationId, expertReportId }) => {
  const isEditable = !row.isTotal && !row.isParent;

  // Набор складывается правилом расчёта — выбирать в нём значение не предлагается. Ужиматься ему,
  // в отличие от балла и одного значения, не запрещено: перечисление переносится по строкам,
  // а не выдавливает наименование блока
  if (row.values?.length) {
    return (
      <span className="text-muted-foreground text-right">{getExpertReportResultText({ values: row.values })}</span>
    );
  }

  // Справочник блока приходит с оценкой, даже когда значение получить не удалось,
  // поэтому по нему и видно, чем блок оценивается
  if (row.possibleValues?.length || row.value !== null || row.manualValue !== null) {
    if (isEditable && row.possibleValues?.length) {
      return (
        <EvaluationBlockValueEditor
          conversationId={conversationId}
          expertReportId={expertReportId}
          blockId={row.blockId}
          value={row.value}
          manualValue={row.manualValue}
          possibleValues={row.possibleValues}
        />
      );
    }
    return <span className="text-muted-foreground shrink-0">{row.manualValue ?? row.value ?? EMPTY_RESULT}</span>;
  }

  // Результата нет вовсе: ни балла, ни значения — прочерк, а не ноль, которого никто не заработал
  if (row.rating === null || row.maxRating === null) {
    return <span className="text-muted-foreground shrink-0">{EMPTY_RESULT}</span>;
  }

  if (!isEditable) {
    return (
      <span className="text-muted-foreground shrink-0">{formatScoreWithUnit(row.rating, row.maxRating, row.unit)}</span>
    );
  }

  return (
    <EvaluationBlockRatingEditor
      conversationId={conversationId}
      expertReportId={expertReportId}
      blockId={row.blockId}
      rating={row.rating}
      maxRating={row.maxRating}
      manualRating={row.manualRating}
      unit={row.unit}
    />
  );
};
